import * as React from "react";

import { cn } from "./utils";

/**
 * Statistic - antd Statistic 대체 컴포넌트
 *
 * 사용 예시:
 *   <Statistic title="총 매출" value={1234567} />
 *   <Statistic title="수익률" value={12.5} suffix="%" />
 *   <Statistic title="포지션" value={-500} prefix={<ArrowDown />} valueClassName="text-blue-600" />
 *   <Statistic title="건수" value={42} formatter={(v) => `${v}건`} />
 */
function Statistic({
  className,
  title,
  value,
  prefix,
  suffix,
  precision,
  formatter,
  valueClassName,
  ...props
}: React.ComponentProps<"div"> & {
  title?: React.ReactNode;
  value?: number | string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  precision?: number;
  formatter?: (value: number | string | undefined) => React.ReactNode;
  valueClassName?: string;
}) {
  const formattedValue = React.useMemo(() => {
    if (formatter) return formatter(value);
    if (value === undefined || value === null) return "-";

    if (typeof value === "number") {
      const num =
        precision !== undefined
          ? value.toFixed(precision)
          : value.toString();
      // 천 단위 콤마
      const parts = num.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join(".");
    }

    return value;
  }, [value, precision, formatter]);

  return (
    <div
      data-slot="statistic"
      className={cn("flex flex-col gap-1", className)}
      {...props}
    >
      {title && (
        <span
          data-slot="statistic-title"
          className="text-xs text-muted-foreground"
        >
          {title}
        </span>
      )}
      <span
        data-slot="statistic-value"
        className={cn("text-2xl font-semibold tabular-nums", valueClassName)}
      >
        {prefix && (
          <span data-slot="statistic-prefix" className="mr-1 inline-flex items-baseline text-sm">
            {prefix}
          </span>
        )}
        {formattedValue}
        {suffix && (
          <span data-slot="statistic-suffix" className="ml-1 text-sm font-normal">
            {suffix}
          </span>
        )}
      </span>
    </div>
  );
}

export { Statistic };
